import React from "react";
import { clamp } from "./format";

/**
 * AllocationEditor — the per-pair allocation control: slider + numeric input,
 * with the pair's minimum viable allocation drawn on the track (SUPERBETA-6).
 *
 * THE FLOOR MARK is the point of this component. gTrade enforces a minimum
 * position size (~$285.71 on BTC), so an allocation one point too low produces
 * a signal that arrives, gets rejected, and moves no money — a silent no-trade
 * that looks exactly like a quiet market. The mark makes that threshold
 * something the member can see before they cross it, rather than something they
 * discover from an absence of trades.
 *
 * The number is the ENGINE'S (`/status.sizingFloors[].minPct`), never ours: it
 * moves with gTrade's live minimum, the member's leverage and their balance.
 * With no floor from the engine the track renders plain — "floors unknown" is
 * shown as nothing at all, never as a guessed threshold (contract §1b rule 6).
 *
 * The amber below-minimum warning itself is composed in WalletSection, so every
 * per-row warning on this tab still comes from one place.
 */

/* Keep in step with the thumb size in automation.css (.sa-slider::-*-thumb). */
const THUMB_PX = 15;

const AllocationEditor = ({
  pair,
  value,
  onChange,
  floor, // { minPct, minCollateralUsd, ... } | null — engine-supplied, may be absent
  below = false, // this allocation is under `floor.minPct`
}) => {
  const pct = clamp(Number(value) || 0, 0, 100);
  const emit = (raw) => onChange(clamp(Number(raw) || 0, 0, 100));
  const markPct = floor ? clamp(floor.minPct, 0, 100) : null;

  return (
    <div className="sa-alloc-edit" data-testid={`sa-alloc-edit-${pair}`}>
      <span className="text-caption text-arc-ink-3">Allocation</span>

      <div className="sa-slider-wrap">
        <input
          type="range"
          className={["sa-slider", below ? "sa-slider-low" : ""].join(" ").trim()}
          min="0"
          max="100"
          step="1"
          value={pct}
          onChange={(e) => emit(e.target.value)}
          aria-label={`${pair} allocation slider`}
          aria-describedby={floor ? `sa-floor-${pair}` : undefined}
        />
        {markPct !== null && (
          /* The threshold itself, placed where the THUMB centre would sit at
             that percentage — a range thumb travels the track inset by half its
             own width, so a plain `left: 25.1%` draws the mark a few pixels left
             of the value it marks. On a control whose entire job is a threshold,
             "close" is the wrong answer.
             aria-hidden because the same fact is stated in words below — a
             screen reader gets the sentence, not a tick. */
          <span
            className="sa-floor-mark"
            style={{ left: `calc(${THUMB_PX / 2}px + (100% - ${THUMB_PX}px) * ${markPct / 100})` }}
            data-min-pct={markPct}
            data-testid={`sa-floor-mark-${pair}`}
            aria-hidden="true"
          />
        )}
      </div>

      <input
        type="number"
        value={value}
        min="0"
        max="100"
        step="1"
        onChange={(e) => emit(e.target.value)}
        className="w-[58px] px-2 py-1 text-sm text-right tnum"
        aria-label={`${pair} allocation percent`}
      />
      <span className="text-sm text-arc-ink-3">%</span>

      {floor && (
        <span
          id={`sa-floor-${pair}`}
          className={["sa-floor-note", below ? "sa-warn" : ""].join(" ").trim()}
        >
          Minimum {floor.minPct}%
        </span>
      )}
    </div>
  );
};

export default AllocationEditor;
